"use client";

import { useRef, useState } from "react";
import { Box, IconButton, Link, Typography, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LinkIcon from "@mui/icons-material/Link";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import FormSection from "./parts/FormSection";
import FormFieldTitle from "./parts/FormFieldTitle";
import usePortfolioUpload from "@lib/candidates/usePortfolioUpload";
import { cloudinaryTransform, PORTFOLIO_EDIT_THUMB_TRANSFORM } from "@lib/cloudinary";
import toast from "react-hot-toast";

const IMAGE_MAX_BYTES = 1024 * 1024;

export default function EditPortfolio({ onClose, open, candidate, onUpdate }) {
  const [items, setItems] = useState(candidate?.portfolio || []);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const { upload, uploadImage, isUploading, isUploadingFile, isUploadingImage } = usePortfolioUpload();

  const handleTitleChange = (id, value) => {
    setItems(items.map((item) => (item.id === id ? { ...item, title: value } : item)));
  };

  const handleUrlChange = (id, value) => {
    setItems(items.map((item) => (item.id === id ? { ...item, url: value } : item)));
  };

  const handleRemove = (id) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleAddLink = () => {
    setItems([...items, { id: crypto.randomUUID(), title: "", url: "", type: "link" }]);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = await upload(candidate.uuid, file);
      setItems([...items, { id: crypto.randomUUID(), title: result.original_filename, url: result.path, type: "file", original_filename: result.original_filename }]);
    } catch (err) {
      console.error("Portfolio upload failed:", err);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 1 MB or smaller.");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    try {
      const result = await uploadImage(candidate.uuid, file);
      setItems([...items, { id: crypto.randomUUID(), title: result.original_filename, url: result.url, type: "image", original_filename: result.original_filename, public_id: result.public_id }]);
    } catch (err) {
      console.error("Portfolio image upload failed:", err);
      toast.error("Image upload failed. Check size and format.");
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleSubmit = () => {
    const validItems = items.filter((item) => item.title && item.url);
    onUpdate({ ...candidate, portfolio: validItems });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit portfolio</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add links to external portfolios or upload files to share with contacts.
            </Typography>
            {items.map((item) => (
              <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: "4px" }}>
                {item.type === "image" ? (
                  <Box component="img" src={cloudinaryTransform(item.url, PORTFOLIO_EDIT_THUMB_TRANSFORM)} alt={item.title || "Portfolio image"} sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 1, flexShrink: 0 }} />
                ) : item.type === "file" ? (
                  <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                ) : (
                  <LinkIcon fontSize="small" color="action" />
                )}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField fullWidth variant="outlined" placeholder="Title" size="small" value={item.title} onChange={(e) => handleTitleChange(item.id, e.target.value)} />
                  {item.type === "link" ? (
                    <TextField fullWidth variant="outlined" placeholder="URL" size="small" value={item.url} onChange={(e) => handleUrlChange(item.id, e.target.value)} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {item.type === "image" ? item.original_filename : item.original_filename || item.url}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => handleRemove(item.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 2, flexWrap: "wrap" }}>
              <Button variant="secondary" size="sm" onClick={handleAddLink}>Add link</Button>
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                {isUploadingFile ? "Uploading..." : "Upload file"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => imageInputRef.current?.click()} disabled={isUploading}>
                {isUploadingImage ? "Uploading..." : "Upload image"}
              </Button>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.ppt,.pptx,.xls,.xlsx" style={{ display: "none" }} />
              <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept=".jpg,.jpeg,.png,.gif,.webp" style={{ display: "none" }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Files up to 10 MB, images up to 1 MB. Reduce image sizes with{" "}
              <Link href="https://tinypng.com/" target="_blank" rel="noopener noreferrer">TinyPng</Link>.
            </Typography>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
